type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export const ORDER_TRACKING_SENT_STORAGE_KEY =
  "reputech_order_tracking_sent_v1";
export const ORDER_TRACKING_SENT_MAX_ENTRIES = 5;
export const ORDER_TRACKING_SENT_TTL_MS = 24 * 60 * 60 * 1000;

export class OrderTrackingAlreadySentError extends Error {
  constructor() {
    // Deliberately no URL: signed proof links must not end up in logs.
    super("Order tracking proof was already sent for this order");
    this.name = "OrderTrackingAlreadySentError";
  }
}

type SentProofRecord = {
  fingerprints: { value: string; created_at: number }[];
};

const getDefaultStorage = (): StorageLike | null => {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const safeJsonParse = <T>(value: string | null): T | null => {
  if (!value) return null;

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

/**
 * Short non-cryptographic fingerprint. Only the fingerprint is persisted so
 * signed proof URLs never land in storage or logs.
 */
export const fingerprintOrderTrackingUrl = (orderTrackingUrl: string): string => {
  let hash = 0x811c9dc5;

  for (let index = 0; index < orderTrackingUrl.length; index += 1) {
    hash ^= orderTrackingUrl.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }

  return hash.toString(16).padStart(8, "0");
};

const readSentFingerprints = (
  now: number,
  storage: StorageLike | null,
): SentProofRecord["fingerprints"] => {
  if (!storage) return [];

  let rawValue: string | null = null;

  try {
    rawValue = storage.getItem(ORDER_TRACKING_SENT_STORAGE_KEY);
  } catch {
    return [];
  }

  const parsed = safeJsonParse<SentProofRecord>(rawValue);

  return Array.isArray(parsed?.fingerprints)
    ? parsed.fingerprints.filter(
        (entry) =>
          entry &&
          typeof entry.value === "string" &&
          typeof entry.created_at === "number" &&
          now - entry.created_at >= 0 &&
          now - entry.created_at < ORDER_TRACKING_SENT_TTL_MS,
      )
    : [];
};

const writeSentFingerprints = (
  fingerprints: SentProofRecord["fingerprints"],
  storage: StorageLike | null,
): void => {
  if (!storage) return;

  try {
    storage.setItem(
      ORDER_TRACKING_SENT_STORAGE_KEY,
      JSON.stringify({ fingerprints } satisfies SentProofRecord),
    );
  } catch {
    // Storage-blocked browsers rely on the in-memory set below.
  }
};

const inMemoryFingerprints = new Map<string, number>();

export const resetOrderTrackingDedupeForTesting = (): void => {
  inMemoryFingerprints.clear();
};

const isMemoryEntryFresh = (createdAt: number | undefined, now: number): boolean =>
  typeof createdAt === "number" &&
  now - createdAt >= 0 &&
  now - createdAt < ORDER_TRACKING_SENT_TTL_MS;

/**
 * Returns true when this signed proof URL was already delivered during the
 * current tab session.
 */
export const hasSentOrderTrackingUrl = (
  orderTrackingUrl: string,
  { now = Date.now(), storage = getDefaultStorage() }: { now?: number; storage?: StorageLike | null } = {},
): boolean => {
  const fingerprint = fingerprintOrderTrackingUrl(orderTrackingUrl);

  if (isMemoryEntryFresh(inMemoryFingerprints.get(fingerprint), now)) {
    return true;
  }

  return readSentFingerprints(now, storage).some(
    (entry) => entry.value === fingerprint,
  );
};

export const markOrderTrackingUrlAsSent = (
  orderTrackingUrl: string,
  { now = Date.now(), storage = getDefaultStorage() }: { now?: number; storage?: StorageLike | null } = {},
): void => {
  const fingerprint = fingerprintOrderTrackingUrl(orderTrackingUrl);
  inMemoryFingerprints.set(fingerprint, now);

  const nextEntries = [
    ...readSentFingerprints(now, storage).filter(
      (entry) => entry.value !== fingerprint,
    ),
    { value: fingerprint, created_at: now },
  ].slice(-ORDER_TRACKING_SENT_MAX_ENTRIES);

  writeSentFingerprints(nextEntries, storage);
};

/**
 * Releases the claim again when delivery failed, so a later retry of the same
 * proof URL stays possible.
 */
export const unmarkOrderTrackingUrlAsSent = (
  orderTrackingUrl: string,
  { storage = getDefaultStorage() }: { storage?: StorageLike | null } = {},
): void => {
  const fingerprint = fingerprintOrderTrackingUrl(orderTrackingUrl);
  inMemoryFingerprints.delete(fingerprint);

  if (!storage) return;

  try {
    const rawValue = storage.getItem(ORDER_TRACKING_SENT_STORAGE_KEY);
    const parsed = safeJsonParse<SentProofRecord>(rawValue);

    if (!Array.isArray(parsed?.fingerprints)) return;

    writeSentFingerprints(
      parsed.fingerprints.filter((entry) => entry?.value !== fingerprint),
      storage,
    );
  } catch {
    // Best effort only.
  }
};
